/**
 * docNumber — เลขที่เอกสารแบบรันอัตโนมัติ ต่อวัน ต่อชนิดเอกสาร
 *
 * ══════════════════════════════════════════════════════════════
 * 🔴 ทำไมต้องเลิกใช้ `Date.now().toString().slice(-6)`
 *
 * มันคือ 6 หลักท้ายของเวลาหน่วยมิลลิวินาที ซึ่งนับถึง 999999 แล้ววนกลับ 0
 * = ครบรอบทุก 1,000 วินาที หรือ 16.7 นาที
 *
 * ผลคือเอกสารสองใบที่ออกห่างกัน 17 นาทีมีสิทธิ์ได้เลขเดียวกัน
 * และเลขไม่เรียงตามเวลา ใบที่ออกทีหลังอาจได้เลขน้อยกว่าใบก่อน
 * ซึ่งเป็นปัญหาจริงกับใบกำกับภาษี
 * ══════════════════════════════════════════════════════════════
 *
 * ══════════════════════════════════════════════════════════════
 * 🔴 ทำไมต้องมีรหัสเครื่องอยู่ในเลข
 *
 * ตัวนับอยู่ใน localStorage ซึ่งเป็นของใครของมันต่อเครื่อง
 * ร้านที่เปิด POS บน Mac และ iPad พร้อมกันจะออก RC-260918-001 ทั้งคู่
 * (เจอจริง 18 ก.ย. 2026: iPad ออกใบแรกเป็น 001 ทั้งที่ Mac ออกไปหลายใบแล้ว)
 *
 * ใส่รหัสเครื่อง 6 ตัวชุดเดียวกับที่อยู่หน้าโทเคนบน QR ลงไปในเลข
 * เครื่องละสายเลข ไม่มีทางชนกัน และดูออกทันทีว่าใบนี้ออกจากเครื่องไหน
 * ══════════════════════════════════════════════════════════════
 *
 * 🔴 แยก peek กับ commit โดยตั้งใจ
 *
 * peekDocNo   = ขอดูเลขถัดไป ยังไม่กิน  -> ใช้ตอนเปิดพรีวิว
 * commitDocNo = ยืนยันว่าใช้เลขนี้จริง  -> ใช้ตอนกดยืนยัน
 *
 * ถ้ากินเลขตั้งแต่ตอนเปิดพรีวิว คนกดดูแล้วกดยกเลิกจะทำให้เลขขาดตอน
 * (ออกใบที่ 007 แล้วข้ามไป 009) ซึ่งสรรพากรถามหาใบที่หายได้
 */

import { getMerchantCode } from './shareToken';

const STORE_KEY = 'moneyma_doc_counters';
// รูปแบบปัจจุบัน: RC-QQNEXF-260918-001
const PATTERN = /^([A-Z]+)-([A-Z0-9]{4,10})-(\d{6})-(\d+)$/;
// รูปแบบเก่าก่อน 18 ก.ย. 2026 ที่ยังไม่มีรหัสเครื่อง: RC-260918-001
const PATTERN_LEGACY = /^([A-Z]+)-(\d{6})-(\d+)$/;

/** yymmdd ตามเวลาเครื่อง ไม่ใช่ UTC — ร้านปิดร้านตามวันของตัวเอง */
function todayYmd(now = new Date()) {
  const yy = String(now.getFullYear() % 100).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
}

function readAll() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch (e) {
    return {};
  }
}

function writeAll(map) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch (e) {
    /* เขียนไม่ได้ก็ยังออกเอกสารได้ ห้ามขวางการขาย */
  }
}

function format(prefix, series, ymd, seq) {
  const tail = `${ymd}-${String(seq).padStart(3, '0')}`;
  return series ? `${prefix}-${series}-${tail}` : `${prefix}-${tail}`;
}

/** กุญแจตัวนับ — แยกตามรหัสเครื่องด้วย เผื่อรหัสเปลี่ยนแล้วเลขจะได้ไม่ชนของเดิม */
function counterKey(prefix, series) {
  return series ? `${prefix}:${series}`.toLowerCase() : String(prefix).toLowerCase();
}

/** แกะเลขที่เอกสารกลับเป็นส่วนประกอบ — รองรับรูปแบบเก่าด้วย คืน null ถ้าไม่ตรงทั้งคู่ */
export function parseDocNo(docNo) {
  const raw = String(docNo || '');
  const m = PATTERN.exec(raw);
  if (m) return { prefix: m[1], series: m[2], ymd: m[3], seq: Number(m[4]) };
  const old = PATTERN_LEGACY.exec(raw);
  if (old) return { prefix: old[1], series: '', ymd: old[2], seq: Number(old[3]) };
  return null;
}

/**
 * เลขถัดไปของชนิดนี้ — เรียกกี่ครั้งก็ได้เลขเดิม จนกว่าจะ commit
 * @param {string} prefix เช่น 'PO' 'RC' 'SO'
 */
export function peekDocNo(prefix, series = getMerchantCode(), now = new Date()) {
  const key = counterKey(prefix, series);
  const ymd = todayYmd(now);
  const cur = readAll()[key];
  const seq = cur && cur.ymd === ymd ? Number(cur.seq || 0) + 1 : 1;
  return format(prefix, series, ymd, seq);
}

/**
 * ยืนยันว่าเลขนี้ถูกใช้ไปแล้ว — ดันตัวนับให้ผ่านเลขนี้
 *
 * เรียกซ้ำด้วยเลขเดิมไม่ทำให้ตัวนับขยับเกิน (idempotent)
 * เพราะทางเดินของเอกสารมีหลายทาง เช่น พิมพ์แล้วค่อยกดยืนยัน
 *
 * @returns {boolean} true เมื่อเลขถูกบันทึกเป็นเลขล่าสุด
 */
export function commitDocNo(docNo) {
  const parsed = parseDocNo(docNo);
  if (!parsed) return false;

  const key = counterKey(parsed.prefix, parsed.series);
  const all = readAll();
  const cur = all[key];

  if (cur && cur.ymd === parsed.ymd && Number(cur.seq || 0) >= parsed.seq) return false;
  // วันเก่ากว่าที่บันทึกไว้ = เครื่องปรับเวลาถอยหลัง อย่าให้ตัวนับถอยตาม
  if (cur && cur.ymd > parsed.ymd) return false;

  all[key] = { ymd: parsed.ymd, seq: parsed.seq };
  writeAll(all);
  return true;
}

/** เลขล่าสุดที่ออกไปแล้วของวันนี้ — เอาไปโชว์ในหน้าตั้งค่าได้ */
export function lastDocNo(prefix, series = getMerchantCode(), now = new Date()) {
  const cur = readAll()[counterKey(prefix, series)];
  if (!cur || cur.ymd !== todayYmd(now)) return '';
  return format(prefix, series, cur.ymd, cur.seq);
}
